import axios, { AxiosError } from 'axios';
import { Environment } from '../../../environment';
import { Api } from '../axiosApi';

export type TStatusPagamentoEmpresa = 'pagante' | 'naopaga';

export interface IEmpresaListagem {
  idempresa: string;
  nome: string;
  fantasia: string;
  cnpj: string;
  telefone: string | null;
  whatsapp: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  uf: string | null;
  cep: string | null;
  banco: string | null;
  status: boolean;
  // Módulos contratados — o ERP esconde do menu os que estiverem desligados.
  // Só vêm no getById (não na listagem).
  modulovendas?: boolean;
  modulocadastros?: boolean;
  moduloestoque?: boolean;
  modulofinanceiro?: boolean;
  modulofiscal?: boolean;
  moduloservicos?: boolean;
  // Sub-flag dentro de Vendas: liga/desliga só as telas de comissão ("Minhas Vendas" e
  // "Vendas da Equipe"), sem mexer no resto do módulo Vendas (PDV, gráfico de evolução).
  comissaoativa?: boolean;
  // Bloqueio manual do acesso ao ERP, independente de `status` — trava temporariamente
  // (ex.: mensalidade atrasada) sem precisar desativar o cadastro da empresa.
  sistematravado?: boolean;
  // Só vêm preenchidos na listagem (GetAll) — resumo de mensalidade da empresa.
  totalparcelas?: number;
  totalpagas?: number;
  logo?: string | null;
}

export type IEmpresaFormData = Omit<IEmpresaListagem, 'idempresa' | 'totalparcelas' | 'totalpagas' | 'logo'>;

export interface IResumoEmpresas {
  total: number;
  pagantes: number;
  naopagam: number;
}

type TComTotalCount = {
  data: IEmpresaListagem[];
  totalCount: number;
};

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      return new Error(axiosError.response.data?.errors?.default || 'Erro desconhecido');
    } else if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const getAll = async (page = 1, filter = '', limit = Environment.LIMITE_DE_LINHAS, statuspagamento?: TStatusPagamentoEmpresa): Promise<TComTotalCount | Error> => {
  try {
    const { data, headers } = await Api.get('/empresas', { params: { page, limit, filter, statuspagamento } });
    return {
      data,
      totalCount: Number(headers['x-total-count'] || 0),
    };
  } catch (error) {
    return tratarErro(error);
  }
};

const resumo = async (filter = ''): Promise<IResumoEmpresas | Error> => {
  try {
    const { data } = await Api.get('/empresas/resumo', { params: { filter } });
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

const getById = async (id: string): Promise<IEmpresaListagem | Error> => {
  try {
    const { data } = await Api.get(`/empresas/${id}`);
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

const create = async (dados: IEmpresaFormData): Promise<{ idempresa: string } | Error> => {
  try {
    const { data } = await Api.post('/empresas', dados);
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

const updateById = async (id: string, dados: IEmpresaFormData): Promise<void | Error> => {
  try {
    await Api.put(`/empresas/${id}`, dados);
  } catch (error) {
    return tratarErro(error);
  }
};

export interface IUsuarioDaEmpresa {
  iduser: string;
  nome: string;
  login: string;
  urlimg: string;
  status: boolean;
}

const getUsuarios = async (idempresa: string, filter = ''): Promise<{ data: IUsuarioDaEmpresa[]; totalCount: number } | Error> => {
  try {
    const { data, headers } = await Api.get(`/empresas/${idempresa}/usuarios`, { params: { filter, limit: 200 } });
    return {
      data,
      totalCount: Number(headers['x-total-count'] || 0),
    };
  } catch (error) {
    return tratarErro(error);
  }
};

export const EmpresaAdminService = {
  getAll,
  resumo,
  getById,
  create,
  updateById,
  getUsuarios,
};
